import React, { useState, useEffect, useRef } from 'react';
import Header from './Header'; // adjust path if needed
import Footer from './Footer'; // adjust path if needed

// --- HELPER & UTILITY COMPONENTS ---
const useOnScreen = (options) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const currentRef = ref.current;
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                if (currentRef) {
                    observer.unobserve(currentRef);
                }
            }
        }, options);

        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [ref, options]);

    return [ref, isVisible];
};

const AnimatedSection = ({ children, className, id }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.1 });
    return (
        <section
            ref={ref}
            id={id}
            className={`${className} transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
            {children}
        </section>
    );
};

const PageHeader = ({ title, subtitle }) => (
    <section className="relative py-24 bg-slate-50">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 to-slate-50 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight">{title}</h1>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-600">{subtitle}</p>
        </div>
    </section>
);

// --- MAIN PAGE COMPONENT ---
const PrivacyPolicyPage = () => {
    const policySections = [
        {
            id: 'introduction',
            title: '1. Introduction',
            content: `This Privacy Policy explains how Outreach ("we," "us," or "our") collects, uses, and discloses information about you when you access or use our website, products, and services (collectively, the "Services"), and when you contact our customer service team, engage with us on social media, or otherwise interact with us.`,
        },
        {
            id: 'information-collection',
            title: '2. Information We Collect',
            content: `We collect information you provide directly to us, such as when you create an account, purchase credits, or request customer support. This may include your name, email address, payment information, and any other information you choose to provide. We also collect technical data automatically, such as your IP address, browser type, and usage information.`
        },
        {
            id: 'information-use',
            title: '3. How We Use Your Information',
            content: `We use the information we collect to:
            \n- Provide, maintain, and improve our Services;
            \n- Process transactions and send you related information, including confirmations and invoices;
            \n- Send you technical notices, updates, security alerts, and support messages;
            \n- Respond to your comments, questions, and requests and provide customer service;
            \n- Monitor and analyze trends, usage, and activities in connection with our Services.`
        },
        {
            id: 'information-sharing',
            title: '4. How We Share Your Information',
            content: `We do not share your personal information with third parties except in the following circumstances:
            \n- With vendors, consultants, and other service providers who need access to such information to carry out work on our behalf;
            \n- In response to a request for information if we believe disclosure is in accordance with, or required by, any applicable law or legal process;
            \n- If we believe your actions are inconsistent with our user agreements or policies, or to protect the rights, property, and safety of Outreach or others.`
        },
        {
            id: 'data-security',
            title: '5. Data Security',
            content: `We take reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction. However, no internet or email transmission is ever fully secure or error-free.`
        },
        {
            id: 'your-rights',
            title: '6. Your Rights',
            content: `Depending on your location, you may have certain rights regarding your personal information, such as the right to access, correct, or delete your data. Residents of India have rights under applicable data protection laws. To exercise these rights, please contact us using the information below.`
        },
        {
            id: 'policy-changes',
            title: '7. Changes to This Policy',
            content: `We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the policy and, in some cases, we may provide you with additional notice (such as adding a statement to our homepage or sending you a notification).`
        },
        {
            id: 'contact-us',
            title: '8. Contact Us',
            content: `If you have any questions about this Privacy Policy, please contact us at: privacy@hlfppt.org.
            
           `
        },
    ];

    return (
        <>
            {/* ✅ Header */}
            <Header />

            <main>
                <PageHeader
                    title="Privacy Policy"
                    subtitle="Your privacy is important to us. Here's how we handle your information."
                />

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    <div className="bg-white p-8 sm:p-12 rounded-2xl shadow-xl border border-slate-200">
                        <p className="text-sm text-slate-500 mb-8">Last Updated: September 15, 2025</p>
                        <div className="space-y-10">
                            {policySections.map(section => (
                                <AnimatedSection key={section.id} id={section.id}>
                                    <h2 className="text-2xl font-bold text-slate-900 mb-4">{section.title}</h2>
                                    <div className="prose max-w-none text-slate-600 whitespace-pre-line">
                                        <p>{section.content}</p>
                                    </div>
                                </AnimatedSection>
                            ))}
                        </div>
                    </div>
                </div>
            </main>

            {/* ✅ Footer */}
            <Footer />
        </>
    );
};

export default PrivacyPolicyPage;
